<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatApiError } from '@yuksalish/api-client'
import { SmartPickerModal, type SmartPickerOption } from '@yuksalish/ui'
import { useShopfloorApi } from '../composables/useShopfloorApi'
import type { ProductOption } from '../types/shopfloor'

const props = defineProps<{
  open: boolean
  title?: string
  selectedId?: number | null
}>()

const emit = defineEmits<{
  close: []
  select: [product: ProductOption]
}>()

const { t } = useI18n()
const { listProducts } = useShopfloorApi()

const PER_PAGE = 20

const searchValue = ref('')
const items = ref<ProductOption[]>([])
const loading = ref(false)
const error = ref('')
const page = ref(1)

const totalPages = computed(() => Math.max(1, Math.ceil(items.value.length / PER_PAGE)))
const options = computed<SmartPickerOption[]>(() =>
  items.value
    .slice((page.value - 1) * PER_PAGE, page.value * PER_PAGE)
    .map((product) => ({
      id: product.id,
      title: product.name,
      subtitle: product.sku ?? product.product_sku ?? undefined,
      meta: product.type ?? product.category ?? '',
    })),
)

async function load() {
  loading.value = true
  error.value = ''
  try {
    items.value = await listProducts(searchValue.value.trim())
    page.value = 1
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

function openModal() {
  searchValue.value = ''
  items.value = []
  error.value = ''
  page.value = 1
  void load()
}

watch(() => props.open, (isOpen) => {
  if (isOpen) openModal()
})

watch(searchValue, () => {
  void load()
})

onMounted(() => {
  if (props.open) openModal()
})
</script>

<template>
  <SmartPickerModal
    :open="open"
    :title="title || t('shopfloor.knittingOutput.modelPickerTitle')"
    :eyebrow="t('shopfloor.knittingOutput.modelPickerEyebrow')"
    :search-label="t('shopfloor.knittingOutput.modelSearch')"
    :search-placeholder="t('shopfloor.knittingOutput.modelSearchPlaceholder')"
    :search-value="searchValue"
    :loading="loading"
    :error-message="error"
    :empty-message="t('shopfloor.knittingOutput.noModels')"
    :items="options"
    :page="page"
    :total-pages="totalPages"
    :selected-id="selectedId ?? null"
    :page-label="t('shopfloor.knittingOutput.modelPage', { count: items.length, page, total: totalPages })"
    :close-aria-label="t('shopfloor.knittingOutput.closeModelPicker')"
    :retry-label="t('shopfloor.state.retry')"
    :previous-label="t('shopfloor.pagination.previous')"
    :next-label="t('shopfloor.pagination.next')"
    :cancel-label="t('shopfloor.assignments.cancel')"
    @close="emit('close')"
    @retry="load"
    @previous="page = Math.max(1, page - 1)"
    @next="page = Math.min(totalPages, page + 1)"
    @select="(option: SmartPickerOption) => {
      const match = items.find((product) => String(product.id) === String(option.id))
      if (match) emit('select', match)
    }"
    @update:search-value="(value: string) => (searchValue = value)"
  >
    <template #loading>{{ t('shopfloor.knittingOutput.modelLoading') }}</template>
  </SmartPickerModal>
</template>
